import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';import { Card } from 'primereact/card';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';
import ChatBot from './ChatBot/ChatBot';
import SummaryCardsContainer from './shared/SummaryCardsContainer';
import AddAccountModal from './AddAccountModal';
import { useTransactions, invalidateAllTransactionCaches } from './useTransactions';
import { getAllAccounts } from '../api/accounts';
import apiClient from '../api/client';
import './OverviewPage.css';

const OverviewPage = ({ username, setModalOpen }) => {
  const navigate = useNavigate();
  const [expandedHoldings, setExpandedHoldings] = useState({});  const [selectedAccount, setSelectedAccount] = useState('all');
  const [accounts, setAccounts] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Financial data states
  const [netWorthData, setNetWorthData] = useState([]);
  const [assetsData, setAssetsData] = useState([]);
  const [liabilitiesData, setLiabilitiesData] = useState([]);
  const [holdingsData, setHoldingsData] = useState([]);
  const [taxSummary, setTaxSummary] = useState({});
  const [userContext, setUserContext] = useState({});
  const [recentTransactions, setRecentTransactions] = useState([]);
  
  // Modal states
  const [showAssetsModal, setShowAssetsModal] = useState(false);
  const [showLiabilitiesModal, setShowLiabilitiesModal] = useState(false);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  
  // Detailed data for modals
  const [detailedAssets, setDetailedAssets] = useState([]);
  const [detailedLiabilities, setDetailedLiabilities] = useState([]);

  // Modal handlers
  const handleAddAccount = () => {
    setShowAddAccountModal(true);
  };

  const handleCloseAddAccountModal = () => {
    setShowAddAccountModal(false);
  };

  const handleAccountAdded = () => {
    // Refresh data when a new account is added
    fetchAllData();
  };

  const handleSnapTradeOpen = () => {
    // Open SnapTrade modal using the same system as the profile page
    if (setModalOpen) {
      setModalOpen({
        open: true,
        type: "account",
      });
    }
  };

  // Use existing hooks for real data
  const { data: capitalGains, loading: gainsLoading } = useTransactions(
    "transactions/getCurrentYearTaxableProfit", 
    username
  );
  const { data: capitalLosses, loading: lossesLoading } = useTransactions(
    "transactions/getCurrentYearTaxableLoss", 
    username
  );
  const { data: potentialLosses, loading: potentialLossesLoading } = useTransactions(
    "transactions/getPotentialLossOpportunities", 
    username
  );
  const { data: washSaleData, loading: washSaleLoading } = useTransactions(
    "transactions/getPastWashSaleTransactions", 
    username
  );

  useEffect(() => {
    if (username) {
      fetchAllData();
    }
  }, [username, selectedAccount]);

  // Process financial data when accounts change
  useEffect(() => {
    if (accounts.length > 0) {
      processFinancialData();
    }
  }, [accounts]);

  // Update tax summary when capital gains/losses data changes
  useEffect(() => {
    if (capitalGains || capitalLosses || potentialLosses || washSaleData) {
      calculateTaxSummary();
    }
  }, [capitalGains, capitalLosses, potentialLosses, washSaleData, userContext]);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Invalidate cache on manual refresh
      console.log('Manual refresh - invalidating transaction caches');
      invalidateAllTransactionCaches();
      
      await Promise.all([
        fetchAccounts(),
        fetchContextData(),
        fetchRecentTransactions()
      ]);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load overview data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const data = await getAllAccounts();
      setAccounts(data);
      const options = [
        { label: 'All Accounts', value: 'all' },
        ...data.map(account => ({
          label: account.institution,
          value: account.id
        }))
      ];
      setAccountOptions(options);
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Failed to load account data');
    }
  };

  const fetchContextData = async () => {
    try {
      const response = await apiClient.get('/api/v1/context');
      if (response.data) {
        processContextData(response.data);
      }
    } catch (err) {
      console.error('Error fetching context data:', err);
    }
  };

  const fetchRecentTransactions = async () => {
    try {
      const response = await apiClient.get('/api/v1/transactions/settled', { params: { page: 0, size: 10 } });
      const items = response.data?.items ?? response.data ?? [];
      if (Array.isArray(items)) {
        setRecentTransactions(items);
      }
    } catch (err) {
      console.error('Error fetching recent transactions:', err);
    }
  };

  const processContextData = (contextArray) => {
    const contextMap = {};
    contextArray.forEach(item => {
      contextMap[item.contextKey] = {
        value: item.contextValue,
        confidence: item.confidenceScore,
        source: item.source,
        lastUpdated: item.lastUpdated
      };
    });

    // Extract key metrics
    const netWorth = parseFloat(contextMap.net_worth?.value || contextMap['financialSituation.netWorth']?.value || '0');
    const totalAccounts = parseInt(contextMap.total_accounts?.value || '0');
    const activeAccounts = parseInt(contextMap.total_accounts?.value || '0');
    const totalTransactions = parseInt(contextMap.total_transactions?.value || '0');
    const recentTransactions = parseInt(contextMap.recent_transactions_6m?.value || '0');
    const uniqueSymbols = parseInt(contextMap.unique_symbols_traded?.value || '0');
    const avgTransactionSize = parseFloat(contextMap.avg_transaction_size?.value || '0');
    const tradingFrequency = contextMap.trading_frequency?.value || 'unknown';
    const isDayTrader = contextMap.is_day_trader?.value === 'true';
    const riskTolerance = contextMap.risk_tolerance?.value || contextMap.inferred_risk_tolerance?.value || 'moderate';
    const investmentGoal = contextMap.investment_goal?.value || contextMap.inferred_investment_goal?.value || 'general_investing';
    const investmentExperience = contextMap.investment_experience?.value || 'beginner';
    const taxEfficiency = contextMap.tax_efficiency?.value || 'medium';
    const portfolioConcentration = contextMap.portfolio_concentration?.value || 'medium';
    const volatilityPreference = contextMap.volatility_preference?.value || 'medium';
    const hasWashSalePatterns = contextMap.has_wash_sale_patterns?.value === 'true';
    const shortTermRatio = parseFloat(contextMap.short_term_ratio?.value || '0');
    const federalShortTermRate = parseFloat(contextMap.federal_short_term_tax_rate?.value || '0');
    const federalLongTermRate = parseFloat(contextMap.federal_long_term_tax_rate?.value || '0');
    const stateTaxRate = parseFloat(contextMap.state_tax_rate?.value || '0');
    const state = contextMap.state?.value || 'Unknown';
    const filingStatus = contextMap.filing_status?.value || 'single';
    const incomeBracket = contextMap.income_bracket?.value || 'unknown';

    setUserContext({
      netWorth,
      totalAccounts,
      activeAccounts,
      totalTransactions,
      recentTransactions,
      uniqueSymbols,
      avgTransactionSize,
      tradingFrequency,
      isDayTrader,
      riskTolerance: riskTolerance.charAt(0).toUpperCase() + riskTolerance.slice(1),
      investmentGoal: investmentGoal.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      investmentExperience: investmentExperience.charAt(0).toUpperCase() + investmentExperience.slice(1),
      taxEfficiency: taxEfficiency.charAt(0).toUpperCase() + taxEfficiency.slice(1),
      portfolioConcentration: portfolioConcentration.charAt(0).toUpperCase() + portfolioConcentration.slice(1),
      volatilityPreference: volatilityPreference.charAt(0).toUpperCase() + volatilityPreference.slice(1),
      hasWashSalePatterns,
      shortTermRatio: (shortTermRatio * 100).toFixed(1) + '%',
      federalShortTermRate,
      federalLongTermRate,
      stateTaxRate,
      state,
      filingStatus: filingStatus.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      incomeBracket,
      lastSyncDate: new Date().toISOString().split('T')[0],
      lastLogin: new Date().toISOString()
    });
  };

  const processFinancialData = () => {
    if (accounts.length === 0) return;
    
    const { assets, liabilities, holdings } = processAccountsData(accounts);
    
    setAssetsData(assets);
    setLiabilitiesData(liabilities);
    setHoldingsData(holdings);
    
    generateNetWorthTrend();
    processDetailedData(accounts);
  };
  const processAccountsData = (accountsData) => {
    const assets = [];
    const liabilities = [];
    const holdingsArray = [];
    const assetCategories = {};
    const liabilityCategories = {};
    const accountHoldings = {};
    
    let totalAssets = 0;
    let totalLiabilities = 0;
    let realEstateValue = 0;
    let realEstateDebt = 0;

    console.log("Processing accounts data:", accountsData);

    
    accountsData = [...accountsData];
    accountsData.forEach(account => {
      const institution = account.institution;
      
      if (!accountHoldings[institution]) {
        accountHoldings[institution] = { 
          stocks: 0, 
          bonds: 0, 
          cash: 0, 
          realEstate: 0,
          retirement: 0,
          debt: 0
        };
      }

      if (account.subAccounts && Array.isArray(account.subAccounts)) {
        account.subAccounts.forEach(subAccount => {
          const amount = parseFloat(subAccount.amount) || 0;
          const owner = subAccount.owner;
          const subAccountName = subAccount.name;

          console.log("Processing subAccount:", { institution, owner, subAccountName, amount });

          if (owner === "Debt" || institution === "Debts") {
            const category = getLiabilityCategory(subAccountName);
            
            // Add ALL debt to liabilities (including mortgage)
            if (!liabilityCategories[category]) {
              liabilityCategories[category] = 0;
            }
            liabilityCategories[category] += amount;
            totalLiabilities += amount;
            
            accountHoldings[institution].debt += amount;
            
            // Track real estate debt for net calculation
            if (category === "Mortgage") {
              realEstateDebt += amount;
            }
          } else if (institution === "Properties" || owner === null) {
            // Add real estate value to total assets (gross)
            totalAssets += amount;
            if (!assetCategories["Real Estate"]) {
              assetCategories["Real Estate"] = 0;
            }
            assetCategories["Real Estate"] += amount;
            accountHoldings[institution].realEstate += amount;
            realEstateValue += amount;
          } else if (institution === "Retirement" || owner === "Retirement") {
            totalAssets += amount;
            if (!assetCategories["Retirement"]) {
              assetCategories["Retirement"] = 0;
            }
            assetCategories["Retirement"] += amount;
            accountHoldings[institution].retirement += amount;
          } else if (owner === "SnapTrade") {
            totalAssets += amount;
            if (!assetCategories["Equity Investments"]) {
              assetCategories["Equity Investments"] = 0;
            }
            assetCategories["Equity Investments"] += amount;
            accountHoldings[institution].stocks += amount;
          } else {
            totalAssets += amount;
            if (!assetCategories["Cash"]) {
              assetCategories["Cash"] = 0;
            }
            assetCategories["Cash"] += amount;
            accountHoldings[institution].cash += amount;
          }
        });
      }
    });

    // For portfolio distribution, show net real estate
    const netRealEstate = realEstateValue - realEstateDebt;
    if (netRealEstate > 0) {
      // Update the Real Estate category to show net value for portfolio distribution
      assetCategories["Real Estate"] = netRealEstate;
    }
    console.log("Processed data:", { totalAssets, totalLiabilities, realEstateValue, realEstateDebt, netRealEstate, accountHoldings });

    // Define specific colors for each asset category
    const getAssetColor = (categoryName) => {
      switch (categoryName) {
        case "Real Estate":
          return "#264653"; // Retirement's color (dark teal)
        case "Retirement":
          return "#2A9D8F"; // Investment's color (light teal)
        case "Equity Investments":
          return "#E76F51"; // Real Estate's color (orange)
        case "Cash":
          return "#F4A261"; // Keep original color
        default:
          return "#2A9D8F"; // Default color
      }
    };

    // Convert to chart data with specific color mapping
    Object.entries(assetCategories).forEach(([name, value]) => {
      if (value > 0) {
        assets.push({
          name,
          value,
          color: getAssetColor(name)
        });
      }
    });

    // Keep liability colors as they were
    const colors = ["#2A9D8F", "#264653", "#E76F51", "#F4A261", "#2A9D8F", "#264653"];
    Object.entries(liabilityCategories).forEach(([name, value], index) => {
      if (value > 0) {
        liabilities.push({
          name,
          value,
          color: colors[index % colors.length]
        });
      }
    });

    // Convert account holdings to chart data with sub-accounts
    // Convert account holdings to chart data with sub-accounts
    Object.entries(accountHoldings).forEach(([account, holdings]) => {
      const totalValue = Object.values(holdings).reduce((sum, val) => sum + (val || 0), 0);
      if (totalValue !== 0) { // Changed from > 0 to !== 0 to include debt accounts
        // Get sub-accounts for this institution
        const subAccounts = [];
        accountsData.forEach(acc => {
          if (acc.institution === account && acc.subAccounts) {
            acc.subAccounts.forEach(subAcc => {
              const amount = parseFloat(subAcc.amount) || 0;
              if (amount !== 0) { // Changed from > 0 to !== 0 to include negative amounts
                subAccounts.push({
                  name: subAcc.name,
                  amount: amount,
                  owner: subAcc.owner,
                  category: getAssetCategory(account, subAcc.owner)
                });
              }
            });
          }
        });
        
        holdingsArray.push({
          account,
          totalValue,
          subAccounts,
          ...holdings
        });
      }
    });    return { assets, liabilities, holdings: holdingsArray };
  };
  
  const getLiabilityCategory = (subAccountName) => {
    if (subAccountName.toLowerCase().includes('mortgage') || subAccountName.toLowerCase().includes('pennymac')) {
      return 'Mortgage';
    } else if (subAccountName.toLowerCase().includes('chase') && subAccountName.toLowerCase().includes('loan')) {
      return 'Mortgage'; // Treat Chase Loan as mortgage for real estate
    } else if (subAccountName.toLowerCase().includes('loan')) {
      return 'Loans';
    } else if (subAccountName.toLowerCase().includes('credit')) {
      return 'Credit Cards';
    } else {
      return 'Other Debt';
    }
  };
  const generateNetWorthTrend = () => {
    // Use the actual net worth from our calculation instead of chart data
    const currentAssets = netWorth.assets;
    const currentLiabilities = netWorth.liabilities;
    const currentNetWorth = netWorth.assets + netWorth.liabilities; //plus because liabilities are negative

    const currentDate = new Date();
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthName = date.toLocaleDateString('en-US', { month: 'short' });
      const variation = (Math.random() - 0.5) * 0.1;
      const assetsValue = currentAssets * (1 + variation);
      const liabilitiesValue = currentLiabilities * (1 + variation * 0.5);
      
      months.push({
        month: monthName,
        netWorth: assetsValue - liabilitiesValue,
        assets: assetsValue,
        liabilities: liabilitiesValue
      });
    }
    setNetWorthData(months);
  };
  const processDetailedData = (accountsData) => {
    const detailedAssets = [];
    const detailedLiabilities = [];
    let realEstateValue = 0;
    let realEstateDebt = 0;

    accountsData = [...accountsData];

    accountsData.forEach(account => {
      if (account.subAccounts && Array.isArray(account.subAccounts)) {
        account.subAccounts.forEach(subAccount => {
          const amount = parseFloat(subAccount.amount) || 0;
          const owner = subAccount.owner;
          const subAccountName = subAccount.name;

          if (owner === "Debt" || account.institution === "Debts") {
            const category = getLiabilityCategory(subAccountName);
            
            // Add ALL debt to detailedLiabilities (including mortgage)
            detailedLiabilities.push({
              name: subAccountName,
              type: category,
              balance: amount,
              monthlyPayment: calculateMonthlyPayment(amount, category),
              interestRate: getInterestRate(category),
              institution: account.institution,
              lastUpdated: account.lastUpdated
            });
            
            // Track real estate debt
            if (category === "Mortgage") {
              realEstateDebt += amount;
            }
          } else if (account.institution === "Properties" || owner === null) {
            // Track real estate value
            realEstateValue += amount;
          } else {
            const category = getAssetCategory(account.institution, owner);
            detailedAssets.push({
              symbol: getSymbolFromName(subAccountName),
              name: subAccountName,
              quantity: 1,
              value: amount,
              account: account.institution,
              category: category,
              lastUpdated: account.lastUpdated
            });
          }
        });
      }
    });

    // Add net real estate as a single asset
    const netRealEstate = realEstateValue - realEstateDebt;
    if (netRealEstate > 0) {
      detailedAssets.push({
        symbol: "RE",
        name: "Real Estate (Net Equity)",
        quantity: 1,
        value: netRealEstate,
        account: "Properties",
        category: "Real Estate",
        lastUpdated: new Date().toISOString()
      });
    }

    setDetailedAssets(detailedAssets);
    setDetailedLiabilities(detailedLiabilities);
  };

  const getAssetCategory = (institution, owner) => {
    if (institution === 'Properties') return 'Real Estate';
    if (institution === 'Retirement' || owner === 'Retirement') return 'Retirement';
    if (owner === 'SnapTrade') return 'Equity Investments';
    return 'Cash';
  };

  const getSymbolFromName = (name) => {
    const match = name.match(/\b[A-Z]{2,5}\b/);
    return match ? match[0] : name.substring(0, 4).toUpperCase();
  };

  const calculateMonthlyPayment = (amount, category) => {
    if (category === 'Mortgage') return amount * 0.005;
    if (category === 'Loans') return amount * 0.02;
    if (category === 'Credit Cards') return amount * 0.03;
    return amount * 0.01;
  };

  const getInterestRate = (category) => {
    if (category === 'Mortgage') return 3.5;
    if (category === 'Loans') return 6.5;
    if (category === 'Credit Cards') return 18.9;
    return 5.0;
  };

  // Advanced tax savings calculation
  const calculateTaxSavings = (
    shortTermGains,
    shortTermLosses,
    longTermGains,
    longTermLosses,
    washSaleLosses,
    potentialShortTermLoss,
    potentialLongTermLoss,
    shortTermTaxRate,
    longTermTaxRate
  ) => {
    const adjustedShortTermLosses = shortTermLosses + washSaleLosses;
    const adjustedLongTermLosses = longTermLosses;

    let netST = shortTermGains - adjustedShortTermLosses;
    let netLT = longTermGains - adjustedLongTermLosses;

    let stOffset = Math.min(shortTermGains, adjustedShortTermLosses);
    let ltOffset = Math.min(longTermGains, adjustedLongTermLosses);

    let crossOffsetST = 0;
    let crossOffsetLT = 0;

    if (netST < 0 && netLT > 0) {
      const offset = Math.min(Math.abs(netST), netLT);
      crossOffsetLT = offset;
      netST += offset;
      netLT -= offset;
    } else if (netLT < 0 && netST > 0) {
      const offset = Math.min(Math.abs(netLT), netST);
      crossOffsetST = offset;
      netLT += offset;
      netST -= offset;
    }

    const totalOffsetLossUsed = stOffset + ltOffset + crossOffsetST + crossOffsetLT;
    const netGain = Math.max(0, shortTermGains - adjustedShortTermLosses + longTermGains - adjustedLongTermLosses);
    const totalUnrealizedLoss = potentialShortTermLoss + potentialLongTermLoss;
    const capitalGainsSavings = Math.min(netGain, totalUnrealizedLoss);
    const remainingLosses = totalUnrealizedLoss - capitalGainsSavings;
    const incomeDeduction = Math.min(remainingLosses, 3000);
    const totalSavings = (capitalGainsSavings + incomeDeduction) * shortTermTaxRate / 100;

    return {
      capitalGainsOffset: capitalGainsSavings,
      incomeDeduction,
      totalSavings,
      netShortTerm: netST,
      netLongTerm: netLT
    };
  };

  const calculateTaxSummary = () => {
    try {
      if (!capitalGains && !capitalLosses && !potentialLosses && !washSaleData) {
        return;
      }

      const currentYear = new Date().getFullYear();
      let shortTermGains = 0;
      let longTermGains = 0;
      let shortTermLosses = 0;
      let longTermLosses = 0;
      let washSaleLosses = 0;
      let potentialShortTermLoss = 0;
      let potentialLongTermLoss = 0;
      let totalDividends = 0;

      if (capitalGains && capitalGains.length > 0) {
        capitalGains.forEach((transaction) => {
          const transactionYear = new Date(transaction.date).getFullYear();
          if (transactionYear !== currentYear) return;

          const amount = Math.abs(transaction.profitLoss || 0);
          if (transaction.capitalGainTaxType === "SHORT" || transaction.capitalGainTaxType === undefined || transaction.capitalGainTaxType === null) {
            shortTermGains += amount;
          }
          if (transaction.capitalGainTaxType === "LONG") {
            longTermGains += amount;
          }
          if (transaction.type === 'DIVIDEND') {
            totalDividends += amount;
          }
        });
      }

      if (capitalLosses && capitalLosses.length > 0) {
        capitalLosses.forEach((transaction) => {
          const transactionYear = new Date(transaction.date).getFullYear();
          if (transactionYear !== currentYear) return;

          const amount = Math.abs(transaction.profitLoss || 0);
          if (transaction.capitalGainTaxType === "SHORT" || transaction.capitalGainTaxType === undefined || transaction.capitalGainTaxType === null) {
            shortTermLosses += amount;
          }
          if (transaction.capitalGainTaxType === "LONG") {
            longTermLosses += amount;
          }
        });
      }

      if (potentialLosses && potentialLosses.length > 0) {
        potentialLosses.forEach((transaction) => {
          const transactionDate = new Date(transaction.date);
          const currentDate = new Date();
          const differenceInDays = (currentDate - transactionDate) / (1000 * 60 * 60 * 24);
          const amount = Math.abs(transaction.unrealizedProfitLoss || 0);

          if (differenceInDays <= 365) {
            potentialShortTermLoss += amount;
          }
          if (differenceInDays > 365) {
            potentialLongTermLoss += amount;
          }
        });
      }

      if (washSaleData && washSaleData.length > 0) {
        washSaleData.forEach((transaction) => {
          const transactionYear = new Date(transaction.date).getFullYear();
          if (transactionYear !== currentYear) return;
          
          if (transaction.capitalGainTaxType === "SHORT" || transaction.capitalGainTaxType === undefined || transaction.capitalGainTaxType === null) {
            washSaleLosses += Math.abs(transaction.profitLoss || 0);
          }
        });
      }

      const shortTermTaxRate = userContext.federalShortTermRate || 30;
      const longTermTaxRate = userContext.federalLongTermRate || 20;

      const savings = calculateTaxSavings(
        shortTermGains,
        shortTermLosses,
        longTermGains,
        longTermLosses,
        washSaleLosses,
        potentialShortTermLoss,
        potentialLongTermLoss,
        shortTermTaxRate,
        longTermTaxRate
      );

      setTaxSummary({
        shortTermGains,
        longTermGains,
        shortTermLosses,
        longTermLosses,
        totalDividends,
        estimatedTaxSavings: savings.totalSavings,
        capitalGainsOffset: savings.capitalGainsOffset,
        incomeDeduction: savings.incomeDeduction,
        taxLossHarvestingOpportunities: potentialLosses ? potentialLosses.length : 0,
        federalShortTermRate: shortTermTaxRate,
        federalLongTermRate: longTermTaxRate,
        stateTaxRate: userContext.stateTaxRate || 0,
        netShortTerm: savings.netShortTerm,
        netLongTerm: savings.netLongTerm
      });
    } catch (err) {
      console.error('Error calculating tax summary:', err);
    }
  };

  const formatCurrency = (amount) => {
    if (isNaN(amount) || amount === null || amount === undefined) {
      return '$0';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const calculateNetWorth = () => {
    // Use the actual gross values from processAccountsData, not the net values from assetsData
    let grossTotalAssets = 0;
    let grossTotalLiabilities = 0;
    
    // Calculate gross assets from accounts data
    accounts.forEach(account => {
      if (account.subAccounts && Array.isArray(account.subAccounts)) {
        account.subAccounts.forEach(subAccount => {
          const amount = parseFloat(subAccount.amount) || 0;
          const owner = subAccount.owner;
          const institution = account.institution;
          
          if (owner === "Debt" || institution === "Debts") {
            grossTotalLiabilities += amount;
          } else {
            grossTotalAssets += amount;
          }
        });
      }
    });
    
    
    console.log("calculateNetWorth - grossTotalAssets:", grossTotalAssets, "grossTotalLiabilities:", grossTotalLiabilities);
    
    return {
      assets: grossTotalAssets,
      liabilities: grossTotalLiabilities,
      netWorth: grossTotalAssets - grossTotalLiabilities
    };
  };
  
  // Calculate net worth using useMemo to recalculate when data changes
  const netWorth = useMemo(() => calculateNetWorth(), [accounts]);
  

  // Get icon class based on account type
  const getAccountIconClass = (accountName) => {
    const name = accountName.toLowerCase();
    if (name.includes("retirement")) return "retirement";
    if (name.includes("properties")) return "properties";
    if (name.includes("debts")) return "debts";
    if (name.includes("emergency")) return "emergency";
    if (name.includes("robinhood")) return "robinhood";
    if (name.includes("fidelity")) return "fidelity";
    if (name.includes("chase")) return "chase";
    if (name.includes("vanguard")) return "vanguard";
    if (name.includes("schwab")) return "schwab";
    if (name.includes("etrade")) return "etrade";
    return "default";
  };

  // Get appropriate icon for each account type
  const getAccountIcon = (accountName) => {
    const name = accountName.toLowerCase();
    if (name.includes("retirement")) return "pi pi-building";
    if (name.includes("properties")) return "pi pi-home";
    if (name.includes("debts")) return "pi pi-credit-card";
    if (name.includes("emergency")) return "pi pi-shield";
    if (name.includes("robinhood")) return "pi pi-chart-line";
    if (name.includes("fidelity")) return "pi pi-star";
    if (name.includes("chase")) return "pi pi-building";
    if (name.includes("vanguard")) return "pi pi-flag";
    if (name.includes("schwab")) return "pi pi-briefcase";
    if (name.includes("etrade")) return "pi pi-mobile";
    return "pi pi-wallet";
  };  // Toggle expanded state for holdings
  const toggleHoldingExpansion = (accountName) => {
    setExpandedHoldings(prev => ({
      ...prev,
      [accountName]: !prev[accountName]
    }));
  };
  
  // Summary cards data
  const summaryCards = [    
    {
      title: 'Net Worth',
      value: formatCurrency(netWorth.assets - netWorth.liabilities),
      icon: 'pi pi-chart-line'
    },
    {
      title: 'Total Assets',
      value: formatCurrency(netWorth.assets),
      changeType: 'positive',
      icon: 'pi pi-arrow-up'
    },
    {
      title: 'Total Liabilities',
      value: formatCurrency(netWorth.liabilities),
      changeType: 'negative',
      icon: 'pi pi-file'
    },
    {
      title: 'Active Accounts',
      value: userContext.activeAccounts || 0,
      subtitle: `Across ${userContext.totalAccounts || 0} institutions`,
      icon: 'pi pi-clock'
    }
  ];
  const isLoading = loading || gainsLoading || lossesLoading || potentialLossesLoading || washSaleLoading;

  if (isLoading) {
    return (
      <div className="overview-loading">
        <div className="loading-spinner"></div>
        <p>Loading your financial overview...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="overview-error">
        <p>Error: {error}</p>
        <Button label="Retry" onClick={fetchAllData} />
      </div>
    );
  }

  return (
    <div className="overview-page">
      {/* Header Section */}
      <div className="overview-header">
        <div className="header-content">
          <h1>Welcome {username}!</h1>
          <p>Here's your financial overview and portfolio performance.</p>
        </div>
        <div className="overview-controls">
          <Button
            label="Link Account"
            icon="pi pi-plus"
            onClick={handleAddAccount}
            className="link-account-button"
            severity="secondary"
          />
          <Button
            icon="pi pi-refresh"
            onClick={fetchAllData}
            className="refresh-button"
            severity="secondary"
            outlined
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        {summaryCards.map((card, index) => (
          <Card key={index} className="summary-card">
            <div className="card-content">
              <div className="card-header">
                <h3>{card.title}</h3>
                <i className={`${card.icon} card-icon`}></i>
              </div>
              <div className="card-value">{card.value}</div>
              {card.change && (
                <div className={`card-change ${card.changeType}`}>
                  {card.change}
                </div>
              )}
              {card.subtitle && (
                <div className="card-subtitle">{card.subtitle}</div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <Card title="Your net worth progression over the last 6 months" className="chart-card">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={netWorthData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
              <RechartsTooltip formatter={(value) => formatCurrency(value)} />
              <Area type="monotone" dataKey="netWorth" stroke="#2A9D8F" fill="#2A9D8F" fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Current portfolio distribution" className="chart-card">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={assetsData}
                cx="50%"
                cy="50%"                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {assetsData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <RechartsTooltip formatter={(value) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Holdings by Account */}
      <Card title="Account balances and recent performance" className="holdings-card">
        <div className="holdings-list">
          {holdingsData.length > 0 ? (
            holdingsData.map((holding, index) => {
              const totalValue = holding.totalValue || 0;
              const portfolioPercentage = holding.account.toLowerCase().includes("debts") ? (netWorth.liabilities > 0 ? (Math.abs(totalValue) / netWorth.liabilities) * 100 : 0) : (netWorth.assets > 0 ? (totalValue / netWorth.assets) * 100 : 0);
              const isExpanded = expandedHoldings[holding.account];
              const hasSubAccounts = holding.subAccounts && holding.subAccounts.length > 1;
              
              return (
                <div key={index} className="holding-item">
                  <div className={`holding-icon ${getAccountIconClass(holding.account)}`}>
                    <i className={getAccountIcon(holding.account)}></i>
                  </div>
                  <div className="holding-details">
                    <div className="holding-header">
                      <h4>{holding.account}</h4>
                    </div>
                    <div className="holding-balance">{formatCurrency(totalValue)}</div>
                    <div className={`holding-change ${holding.account.toLowerCase().includes("debts") ? "negative" : "positive"}`}>
                      {holding.account.toLowerCase().includes("debts") ? "-2.1% this month" : `+${(portfolioPercentage * 0.03).toFixed(2)}% this month`}
                    </div>
                  </div>
                  <div className="holding-percentage">
                    {portfolioPercentage.toFixed(1)}% of {holding.account.toLowerCase().includes("debts") ? "liabilities" : "portfolio"}
                    {hasSubAccounts && (
                      <Button
                        icon={isExpanded ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                        className="expand-button"
                        onClick={() => toggleHoldingExpansion(holding.account)}
                      />
                    )}
                  </div>
                  {/* Sub-accounts dropdown */}
                  {isExpanded && hasSubAccounts && (
                    <div className="sub-accounts">
                      {holding.subAccounts.map((subAccount, subIndex) => (
                        <div key={subIndex} className="sub-account-item">
                          <div className="sub-account-name">{subAccount.name}</div>
                          <div className="sub-account-amount">{formatCurrency(subAccount.amount)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="no-data-message">
              <p>No account data available. Please add some accounts to see your holdings.</p>
            </div>
          )}
        </div>
      </Card>

      {/* Tax Summary */}
      {/* Tax Summary */}
      <Card title="Tax Summary" className="tax-summary-card">
        <div className="tax-summary-header">
          <h3>2025 Tax Year</h3>
          <div className="tax-header-actions">
            <Button
              label="View More Details"
              icon="pi pi-external-link"
              onClick={() => navigate('/tax-insights')}
              className="p-button-outlined tax-details-button"
            />
          </div>
        </div>
        <div className="tax-cards">
          <div className="tax-card">
            <div className="tax-card-header">
              <h4>Capital Gains</h4>
              <i className="pi pi-arrow-up"></i>
            </div>
            <div className="tax-value positive">{formatCurrency(taxSummary.shortTermGains + taxSummary.longTermGains || 0)}</div>
            <div className="tax-details">
              <div>Realized gains this year</div>
              <div>Long-term: {formatCurrency(taxSummary.longTermGains || 0)}</div>
              <div>Short-term: {formatCurrency(taxSummary.shortTermGains || 0)}</div>
            </div>
          </div>

          <div className="tax-card">
            <div className="tax-card-header">
              <h4>Capital Losses</h4>
              <i className="pi pi-arrow-down"></i>
            </div>
            <div className="tax-value negative">{formatCurrency(taxSummary.shortTermLosses + taxSummary.longTermLosses || 0)}</div>
            <div className="tax-details">
              <div>Realized losses this year</div>
              <div>Available to offset gains</div>
              <div>Net taxable: {formatCurrency((taxSummary.shortTermGains + taxSummary.longTermGains || 0) - (taxSummary.shortTermLosses + taxSummary.longTermLosses || 0))}</div>
            </div>
          </div>

          <div className="tax-card">
            <div className="tax-card-header">
              <h4>Dividends</h4>
              <i className="pi pi-dollar"></i>
            </div>
            <div className="tax-value neutral">{formatCurrency(taxSummary.totalDividends|| 0)}</div>
            <div className="tax-details">
              <div>Dividend income received</div>
              <div>Non-qualified: {formatCurrency((taxSummary.totalDividends || 0) * 0.9)}</div>
              <div>Qualified: {formatCurrency((taxSummary.totalDividends|| 0) * 0.1)}</div>
            </div>
          </div>

          <div className="tax-card">
            <div className="tax-card-header">
              <h4>Tax Savings</h4>
              <i className="pi pi-file"></i>
            </div>
            <div className="tax-value savings">{formatCurrency(taxSummary.estimatedTaxSavings  || 0)}</div>
            <div className="tax-details">
              <div>Through tax optimization strategies</div>
              <div>Tax Loss Harvesting: {formatCurrency((taxSummary.estimatedTaxSavings * 0.87|| 0))}</div>
              <div>Wash Sale avoidance: {formatCurrency((taxSummary.estimatedTaxSavings * 0.13|| 0))}</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Account Information, Risk Profile, and Tax Information */}
      <div className="info-cards">
        <Card title="Your active accounts and transactions" className="info-card">
          <div className="info-item">
            <span>Active Accounts</span>
            <span className="info-value">{userContext.activeAccounts || 5}</span>
          </div>
          <div className="info-item">
            <span>Total Transactions</span>
            <span className="info-value">{userContext.totalTransactions?.toLocaleString() * 5|| 0}</span>
          </div>
          <div className="info-item">
            <span>This Month</span>
            <span className="info-value">{userContext.recentTransactions || 0}</span>
          </div>
        </Card>

        <Card title="Investment experience and goals" className="info-card">
          <div className="info-item">
            <span>Investment Experience</span>
            <span className="info-value">{userContext.investmentExperience || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span>Investment Goal</span>
            <span className="info-value">{userContext.investmentGoal || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span>Risk Tolerance</span>
            <Tag value={userContext.riskTolerance || 'N/A'} severity="warning" />
          </div>
          <div className="info-note">
            Portfolio allocation aligned with risk profile.
          </div>
        </Card>

        <Card title="Filing status and tax details" className="info-card">
          <div className="info-item">
            <span>State</span>
            <span className="info-value">{userContext.state || 'Unknown'}</span>
          </div>
          <div className="info-item">
            <span>Filing Status</span>
            <span className="info-value">{userContext.filingStatus || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span>Income Bracket</span>
            <span className="info-value">${userContext.incomeBracket?.replace('-', ' - $') || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span>Estimated tax rate</span>
            <span className="info-value">{taxSummary.federalShortTermRate || 0}% federal, {taxSummary.stateTaxRate || 0}% state</span>
          </div>
        </Card>
      </div>


      {/* Modals */}
      <Dialog
        header="All Assets"
        visible={showAssetsModal}
        style={{ width: '80vw' }}
        onHide={() => setShowAssetsModal(false)}
      >
        <DataTable value={detailedAssets} paginator rows={10}>
          <Column field="symbol" header="Symbol" />
          <Column field="name" header="Name" />
          <Column field="category" header="Category" />
          <Column field="value" header="Value" body={(rowData) => formatCurrency(rowData.value)} />
          <Column field="account" header="Account" />
          <Column field="lastUpdated" header="Last Updated" />
        </DataTable>
      </Dialog>

      <Dialog
        header="All Liabilities"
        visible={showLiabilitiesModal}
        style={{ width: '80vw' }}
        onHide={() => setShowLiabilitiesModal(false)}
      >
        <DataTable value={detailedLiabilities} paginator rows={10}>
          <Column field="name" header="Name" />
          <Column field="type" header="Type" />
          <Column field="balance" header="Balance" body={(rowData) => formatCurrency(rowData.balance)} />
          <Column field="monthlyPayment" header="Monthly Payment" body={(rowData) => formatCurrency(rowData.monthlyPayment)} />
          <Column field="interestRate" header="Interest Rate" body={(rowData) => `${rowData.interestRate}%`} />
          <Column field="institution" header="Institution" />
          <Column field="lastUpdated" header="Last Updated" />
        </DataTable>
      </Dialog>

      {/* Add Account Modal */}
      <AddAccountModal
        isOpen={showAddAccountModal}
        onClose={handleCloseAddAccountModal}
        onSnapTradeOpen={handleSnapTradeOpen}
        onPropertySave={handleAccountAdded}
        username={username}
      />
    </div>
  );
};

export default OverviewPage;
